import React from "react";
import {
  Box,
  Grid,
  Typography,
  FormControlLabel,
  Checkbox,
  TextField,
} from "@mui/material";
import RegisterStyles from "../../assets/css/register.styles";
import { RegisterProps } from "./registerProps";
import { GeneralStyles } from "../../assets/css/general.styles"; 
import theme from "../../themes/index.en";
import { ValidatableComponent } from "./validatableComponent";
import { RegisterTouched } from "./registerTouched";
import { InvestorModel } from "../../models/models/investorModel";
import { InvestorReportableJurisdictionModel } from "../../models/models/investorReportableJurisdictionModel";

const ReportableJurisdiction: ValidatableComponent = ({
  formData,
  setFormData,
  touched,
  setTouched,
  t,
  isRTL
}) => { 
  
  const reportableJurisdictionQuestions = [
    {
      label: "areYouReportableJurisdiction",
      field: "isReportableJurisdictionResident",
      relatedField: "reportableJurisdiction",
    },
    {
      label:"reportableJurisdictionResidenceAddress", 
      field: "hasResidenceAddress",
      relatedField: "residenceAddress",
    },
    {
      label:"telephoneNumbers",
      field: "hasTelephoneNumbers",
      relatedField: "telephoneNumbers",
    },
    {
      label:"holdMailInstruction",
      field: "hasHoldMailInstruction",
      relatedField: "holdMailInstruction",
    },
    {
      label:"instructionTransferFunds",
      field: "hasInstructionTransferFunds",
      relatedField: "instructionTransferFunds", 
    },
    {
      label:  "powerAttorney",
      field: "hasPowerAttorney",
      relatedField: "powerAttorney", 
    },
  ];

  return (
    <Box
    display="flex"
    flexDirection="column"
    gap={2}
    mb={2}
    pl={5}
    sx={{
      textAlign: isRTL ? "right" : "left",
      direction: isRTL ? "rtl" : "ltr", 
      marginRight: '50px', 
    }}
  >
      <Box sx={RegisterStyles.boxColoredStyle}>
        <Grid container item xs={12} spacing={2}>
          <Grid item xs={12}>
            <Typography variant="body1" sx={RegisterStyles.paragraphStyle}>
               {t('repostableJurisdictionAgreement')}
            </Typography>
            <Typography variant="body1" sx={RegisterStyles.paragraphStyle}>
             {t('repostableJurisdictionClassification')}
            </Typography>
          </Grid>
        </Grid>
      </Box>
      <Box sx={RegisterStyles.boxStyle}>
        <Grid container spacing={2}>
          {touched.reportableJurisdictionQuestions && (
            <Typography
              variant="h6"
              gutterBottom
              p={1}
              sx={{ color: theme.palette.error.main }}
            >
              {t('chooseOneOption')}
            </Typography>
          )}
          {reportableJurisdictionQuestions.map(
            ({ label, field, relatedField }) => (
              <Grid container item xs={12} spacing={1} key={field}>
                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ fontWeight: "500" }}>
                    {t(`${label}`)}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        style={GeneralStyles.checkBoxStyle} 
                        checked={formData.reportableJurisdiction[field as keyof InvestorReportableJurisdictionModel] === true}
                        onChange={(e) =>
                          setFormData((prevData) => ({
                            ...prevData,
                            reportableJurisdiction: {
                              ...prevData.reportableJurisdiction,
                              [field]: e.target.checked ? true : null,
                            },
                          }))
                        }
                      />
                    }
                    label={t('yes')}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        style={GeneralStyles.checkBoxStyle}
                        checked={formData.reportableJurisdiction[field as keyof InvestorReportableJurisdictionModel] === false}
                        onChange={(e) =>
                          setFormData((prevData) => ({
                            ...prevData,
                            reportableJurisdiction: {
                              ...prevData.reportableJurisdiction,
                              [field]: e.target.checked ? false : null,
                              [relatedField]: "",
                            },
                          }))
                        }
                      />
                    }
                    label={t('no')}
                  />
                </Grid>
                <Grid item xs={4}>
                  {formData.reportableJurisdiction[field as keyof InvestorReportableJurisdictionModel] === true && (
                    <TextField
                      fullWidth
                      label={t('pleaseSpecify')} 
                      value={formData.reportableJurisdiction[relatedField as keyof InvestorReportableJurisdictionModel] || ""}
                      onChange={(e) =>
                        setFormData((prevData) => ({
                          ...prevData,
                          reportableJurisdiction: {
                            ...prevData.reportableJurisdiction, 
                            [relatedField]:e.target.value,
                          },
                        }))
                      }
                      InputProps={{
                        inputProps: {
                          dir: isRTL ? 'rtl' : 'ltr',
                        },
                      }}
                      InputLabelProps={{
                        style: { 
                          textAlign: isRTL ? 'right' : 'left',
                          right: isRTL ? 0 : 'auto', 
                          left: isRTL ? 'auto' : 0, 
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: isRTL ? 'flex-start' : 'flex-start',
                          marginRight: isRTL ? '20px' : '0',  
                        },
                      }}
                    />
                  )}
                </Grid>
              </Grid>
            )
          )}
        </Grid>
      </Box>
    </Box>
  );
};

ReportableJurisdiction.validate = (formData: InvestorModel): RegisterTouched => {
  const newTouched = {
    reportableJurisdictionQuestions:
      formData.reportableJurisdiction.isReportableJurisdictionResident === null ||
      (formData.reportableJurisdiction.isReportableJurisdictionResident &&
        formData.reportableJurisdiction.reportableJurisdiction.trim() === "") ||
      formData.reportableJurisdiction.hasResidenceAddress === null ||
      (formData.reportableJurisdiction.hasResidenceAddress &&
        formData.reportableJurisdiction.residenceAddress.trim() === "") ||
      formData.reportableJurisdiction.hasTelephoneNumbers === null ||
      (formData.reportableJurisdiction.hasTelephoneNumbers &&
        formData.reportableJurisdiction.telephoneNumbers.trim() === "") ||
      formData.reportableJurisdiction.hasHoldMailInstruction === null ||
      (formData.reportableJurisdiction.hasHoldMailInstruction &&
        formData.reportableJurisdiction.holdMailInstruction.trim() === "") ||
      formData.reportableJurisdiction.hasInstructionTransferFunds === null ||
      (formData.reportableJurisdiction.hasInstructionTransferFunds &&
        formData.reportableJurisdiction.instructionTransferFunds.trim() === "") ||
      formData.reportableJurisdiction.hasPowerAttorney === null ||
      (formData.reportableJurisdiction.hasPowerAttorney &&
        formData.reportableJurisdiction.powerAttorney.trim() === ""),
  };
  return newTouched;
};

export default ReportableJurisdiction;
